import { useEffect, useState } from "react";
import { continueDemo, readDemoCase } from "./demo-api";
import { getRuntimeOptions, getSession } from "./api";
import { readPendingDemo, savePendingDemo, saveDemoHistorySource } from "./demo-state";
import type { DemoCaseResponse } from "@rnavoyager/contracts";
import type { DemoContinuation } from "./demo-api";
import type { SessionDetail } from "./types";
import { U1A_DEMO_PATH } from "./DemoChat";

export interface ContinuedDemo {
  session: SessionDetail;
  question: string;
  importedContext: string;
  snapshot: DemoCaseResponse;
}
const running = new Map<string, Promise<DemoContinuation>>();
function copyOnce(identityId: string, caseId: string, input: Parameters<typeof continueDemo>[1]) {
  const key = `${identityId}:${input.requestId}:${input.seriesId}`;
  let promise = running.get(key);
  if (!promise) {
    promise = continueDemo(caseId, input).finally(() => running.delete(key));
    running.set(key, promise);
  }
  return promise;
}

export function DemoContinuationScreen({
  identityId,
  onReady,
}: {
  identityId: string;
  onReady: (value: ContinuedDemo) => void;
}) {
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    setError("");
    async function prepare() {
      const pending = readPendingDemo();
      if (!pending) throw new Error("待续接的研究案例不存在");
      const options = await getRuntimeOptions();
      if (!active) return;
      const seriesId = pending.seriesId ?? options.default_series_id;
      if (!seriesId) throw new Error("当前模型目录没有默认系列");
      savePendingDemo({ ...pending, seriesId });
      const response = await copyOnce(identityId, pending.caseId, {
        requestId: pending.requestId,
        seriesId,
        caseDigest: pending.caseDigest,
      });
      if (!active) return;
      if (response.state === "preparing") {
        timer = setTimeout(() => {
          void prepare().catch(fail);
        }, 2000);
        return;
      }
      const [session, snapshot] = await Promise.all([
        getSession(response.sessionId),
        readDemoCase(pending.caseId),
      ]);
      if (!active) return;
      if (snapshot.caseDigest !== pending.caseDigest)
        throw new Error("研究案例已更新，请返回案例后继续");
      const contextFile = response.files.at(-1);
      if (contextFile?.name !== "case-context.json")
        throw new Error("案例完整上下文文件未准备完成");
      saveDemoHistorySource(identityId, session.id, {
        caseId: pending.caseId,
        caseDigest: pending.caseDigest,
      });
      onReady({
        session,
        snapshot,
        question: pending.question,
        importedContext: `${response.context}\n\n完整案例资料：${contextFile.path}（fileId=${contextFile.fileRef.fileId}）。请读取这些导入资料后回答后续问题。`,
      });
    }
    function fail(cause: unknown) {
      if (active) setError(cause instanceof Error ? cause.message : "案例资料准备失败");
    }
    void prepare().catch(fail);
    return () => {
      active = false;
      if (timer) clearTimeout(timer);
    };
  }, [revision, onReady, identityId]);
  return (
    <main className="login-shell">
      <section className="login-card">
        <h1>继续 U1A × U1 snRNA 研究</h1>
        {error ? (
          <>
            <p role="alert">{error}</p>
            <button className="primary" onClick={() => setRevision((value) => value + 1)}>
              重试资料导入
            </button>
          </>
        ) : (
          <p role="status">正在为你的私有会话准备报告、结构文件和案例资料…</p>
        )}
        <a href={U1A_DEMO_PATH}>返回案例</a>
      </section>
    </main>
  );
}
